import React from "react";
import { motion } from "framer-motion";
import { Globe, Shield, BookOpen, Building2, Rocket, ArrowRight } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { InitiativeCard } from "@/components/Cards";
import { bahrainInitiatives } from "@/data/index";

export default function Bahrain() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.BAHRAIN_SKYLINE_2}
            alt="Bahrain Skyline"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4 border border-accent/20">
              National Vision 2030
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
              The <span className="text-primary">Quantum</span> Gateway
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              How the Kingdom of Bahrain is positioning itself as the regional epicenter for the second quantum revolution through strategic investment and global partnerships.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Strategic Pillars */}
      <section className="py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          <motion.div
            whileHover={{ y: -5 }}
            className="p-8 rounded-2xl border border-border bg-card/50 backdrop-blur-sm"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6">
              <Globe className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold mb-3">Regional Hub</h3>
            <p className="text-muted-foreground">
              Leveraging Bahrain's strategic location and advanced digital infrastructure to host the Middle East's first quantum-classical hybrid networks.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="p-8 rounded-2xl border border-border bg-card/50 backdrop-blur-sm"
          >
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent mb-6">
              <Shield className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold mb-3">Cyber Sovereignty</h3>
            <p className="text-muted-foreground">
              Proactively defending national assets through the early adoption of post-quantum cryptographic standards and secure quantum communications.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="p-8 rounded-2xl border border-border bg-card/50 backdrop-blur-sm"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6">
              <BookOpen className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold mb-3">Talent Cultivation</h3>
            <p className="text-muted-foreground">
              Empowering the next generation of Bahraini innovators with specialized academic programs in quantum physics and quantum-AI engineering.
            </p>
          </motion.div>
        </div>

        {/* Initiatives Grid */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold mb-4">National Quantum Initiatives</h2>
              <p className="text-muted-foreground">
                Strategic programs launched in collaboration with Mumtalakat, NCSC, and global technology leaders to drive industrial transformation.
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm font-mono">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Active
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                Upcoming
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {bahrainInitiatives.map((initiative, index) => (
              <motion.div
                key={initiative.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <InitiativeCard initiative={initiative} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Spotlight */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="bg-card border border-border rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <h2 className="text-3xl font-bold mb-6">
                  Strategic Partnership: SandboxAQ & Mumtalakat
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  In a landmark move, Bahrain's sovereign wealth fund has partnered with SandboxAQ to revolutionize drug discovery and materials science. This billion-dollar commitment utilizes quantum-inspired AI to solve previously intractable biological challenges.
                </p>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 bg-primary/20 p-1 rounded">
                      <Rocket className="w-4 h-4 text-primary" />
                    </div>
                    <span>Accelerating life sciences research by 10x</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 bg-primary/20 p-1 rounded">
                      <Building2 className="w-4 h-4 text-primary" />
                    </div>
                    <span>Building a regional biotech hub in Manama</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 bg-primary/20 p-1 rounded">
                      <Shield className="w-4 h-4 text-primary" />
                    </div>
                    <span>Securing the financial sector with PQC</span>
                  </li>
                </ul>
                <button className="group flex items-center gap-2 text-primary font-semibold hover:gap-4 transition-all">
                  View Full Partnership Details <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              <div className="relative min-h-[400px]">
                <img
                  src={IMAGES.RESEARCH_COLLABORATION_3}
                  alt="Quantum Research"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 container mx-auto px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-6">Leading the Second Quantum Revolution</h2>
          <p className="text-xl text-muted-foreground mb-10">
            Bahrain invites researchers, tech companies, and visionaries to join our growing quantum ecosystem. Together, we are building the foundation for the next century of scientific achievement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-primary text-primary-foreground rounded-full font-bold hover:shadow-lg hover:shadow-primary/20 transition-all">
              Contact Investment Board
            </button>
            <button className="px-8 py-4 bg-secondary text-secondary-foreground rounded-full font-bold border border-border hover:bg-muted transition-all">
              Download Roadmap 2030
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
